import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { StrengthClusterView, StrengthConfidence } from '../../../../models/employee';

/**
 * CO-447 — company-wide strength clusters.
 *
 * 🔴 This file used to declare its own `StrengthCluster` interface, duplicated in `../index.tsx`.
 * Both are replaced by the single `StrengthClusterView` in `models/employee.ts`. That was not an
 * import swap: the old client shape was `{ strength, frequency, departments, confidence,
 * emergingTrend }` and the server's is `{ label, peopleCount, ... }`. `frequency` in particular
 * was never a frequency — the server counts DISTINCT employees holding the strength — so the
 * "occurrences" label under the bar was describing a number that does not exist.
 */

interface StrengthClustersSectionProps {
  data: StrengthClusterView[];
  expanded?: boolean;
  onViewMore?: () => void;
  /**
   * CO-449 §3.5.4 — suppression rate as a PERCENT, 0-100 (the server already multiplied by 100;
   * see `CompanyStrengthClustersView.SuppressionRate`). A quiet line, never a gate. Read it:
   * above roughly 30 the detector is over-firing and its matcher needs tightening.
   * An automatic threshold that silently disabled detection would be worse than a number a human
   * reads. Undefined when the server did not send the summary, in which case nothing renders —
   * printing a fake 0% would read as "the detector is perfect".
   */
  suppressionRate?: number;
}

const CONFIDENCE_CLASS: Record<StrengthConfidence, string> = {
  High: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
  Medium: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  Low: 'bg-muted text-muted-foreground border-border',
};

const StrengthClustersSection: React.FC<StrengthClustersSectionProps> = ({
  data,
  expanded = false,
  onViewMore,
  suppressionRate,
}) => {
  const { t } = useTranslation();

  // The bar used to divide by a hardcoded 40, so a company of 12 people could never fill more than
  // a third of it and a company of 200 pinned every bar at 100%. Scale to the largest cluster in
  // the set actually being shown; the bar is a comparison between clusters, not against a constant.
  const maxPeople = Math.max(1, ...(data ?? []).map((c) => c?.peopleCount ?? 0));
  const visible = data?.slice(0, expanded ? data?.length : 3) ?? [];

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Icon name="Sparkles" size={22} className="me-2 text-primary" />
            {t('strengths.clusters.title')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-[60ch]">
            {t('Top recurring strengths across the organization')}
          </p>
        </div>
        <div className="text-end">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {t('strengths.count', { count: data?.length ?? 0 })}
          </span>
          {typeof suppressionRate === 'number' && (
            <p className="mt-1 text-xs text-muted-foreground whitespace-nowrap" data-testid="suppression-rate">
              {/* W3-H's `strengths.suppressionRate` is a plain LABEL ("Suppression rate") with no
                  {{rate}} placeholder, unlike spec §3.5.4's `t('strengths.suppressionRate',
                  { rate })`. Passing the interpolation would have printed the label and silently
                  dropped the number, so the value is rendered beside it instead. */}
              {t('strengths.suppressionRate')} {Math.round(suppressionRate)}%
            </p>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        // The old implementation had NO empty state: with `data` permanently `[]` it rendered an
        // empty grid under a heading, which reads as a broken page rather than a young company.
        <div className="rounded-lg border border-border bg-muted py-12 text-center" data-testid="clusters-empty">
          <Icon name="Sparkles" size={40} className="mx-auto mb-3 text-muted-foreground" />
          <p className="mx-auto max-w-[52ch] text-sm text-muted-foreground">{t('strengths.clusters.empty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((cluster, index) => (
            <div
              key={`${cluster?.label}-${index}`}
              className="border border-border rounded-lg bg-card p-4 transition-colors hover:border-primary"
              data-testid="strength-cluster"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Icon name="Sparkles" size={16} className="text-primary" />
                    <h3 className="text-base font-semibold text-foreground">{cluster?.label}</h3>
                    {cluster?.emergingTrend && (
                      <span className="inline-flex items-center rounded-full border border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                        <Icon name="TrendingUp" size={10} className="me-1" />
                        {t('strengths.clusters.emerging')}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center">
                      <Icon name="Users" size={12} className="me-1" />
                      {t('strengths.clusters.people', { count: cluster?.peopleCount ?? 0 })}
                    </span>
                    <span className="flex items-center">
                      <Icon name="Building2" size={12} className="me-1" />
                      {cluster?.departments?.length ?? 0} {t('departments')}
                    </span>
                  </div>
                </div>
                <span
                  className={`inline-flex shrink-0 items-center rounded-full border px-2 py-1 text-xs font-medium ${CONFIDENCE_CLASS[cluster?.confidence] ?? CONFIDENCE_CLASS.Low}`}
                >
                  {t(`strengths.confidence.${(cluster?.confidence ?? 'Low').toLowerCase()}`)}
                </span>
              </div>

              <div className="mb-3">
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                  role="img"
                  aria-label={t('strengths.clusters.people', { count: cluster?.peopleCount ?? 0 })}
                >
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.round(((cluster?.peopleCount ?? 0) / maxPeople) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {cluster?.departments?.slice(0, 3)?.map((dept, deptIndex) => (
                  <span
                    key={deptIndex}
                    className="rounded border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  >
                    {dept}
                  </span>
                ))}
                {(cluster?.departments?.length ?? 0) > 3 && (
                  <span className="px-2 py-0.5 text-xs text-muted-foreground">
                    +{(cluster?.departments?.length ?? 0) - 3} {t('more')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!expanded && (data?.length ?? 0) > 3 && (
        <div className="mt-4 text-center">
          {onViewMore ? (
            <button
              onClick={onViewMore}
              className="text-sm font-medium text-primary hover:underline"
            >
              {t('View All')} {data?.length} {t('Strengths')}
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">{t('Showing')} 3 {t('of')} {data?.length} {t('strengths')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StrengthClustersSection;
